/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Add24Regular, Delete24Regular, Edit24Regular, Eye24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { MeilensteinItem } from '../../models/MeilensteinItem';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    toolbar: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        alignItems: 'center',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
        flex: 1,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    errorMessage: {
        color: tokens.colorStatusDangerForeground1,
        marginBottom: tokens.spacingVerticalL,
    },
    colActions: {
        width: '80px',
        minWidth: '80px',
    },
    colNumber: {
        width: '120px',
        minWidth: '120px',
    },
    colDate: {
        width: '120px',
        minWidth: '120px',
    },
    sectionHeader: {
        fontWeight: 700,
        fontSize: tokens.fontSizeBase400,
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorBrandForeground1,
    },
    sectionDescription: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3,
        marginBottom: tokens.spacingVerticalM,
    },
    readOnlyField: {
        padding: tokens.spacingVerticalS,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        fontWeight: 500,
    },
    dragHandle: {
        cursor: 'grab',
        color: tokens.colorNeutralForeground3,
        userSelect: 'none',
        '&:active': {
            cursor: 'grabbing',
        },
    },
    draggingRow: {
        opacity: 0.5,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    dropPositionBefore: {
        borderTop: '3px solid #107c10',
        boxShadow: 'inset 0 3px 0 -1px #107c10',
    },
    dropPositionAfter: {
        borderBottom: '3px solid #ff8c00',
        boxShadow: 'inset 0 -3px 0 -1px #ff8c00',
    },
    dropBadge: {
        position: 'absolute',
        left: '8px',
        display: 'flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '3px',
        whiteSpace: 'nowrap',
        zIndex: 10,
    },
    dropBadgeBefore: {
        backgroundColor: '#107c10',
        color: 'white',
        top: '-12px',
    },
    dropBadgeAfter: {
        backgroundColor: '#ff8c00',
        color: 'white',
        bottom: '-12px',
    },
});

interface MeilensteinForm {
    itemId?: number;
    projektId: number;
    bezeichnung: string;
    beschreibung: string;
    datum: Date | null;
    insertAfterId: number;
}

interface ProjektmanagementMeilensteineProps {
    project: ProjektItem | null;
}

const ProjektmanagementMeilensteine: React.FC<ProjektmanagementMeilensteineProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [meilensteine, setMeilensteine] = useState<MeilensteinItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isViewOnly, setIsViewOnly] = useState(false);

    // Drag-and-drop states
    const [draggedItem, setDraggedItem] = useState<any | null>(null);
    const [dragOverItem, setDragOverItem] = useState<any | null>(null);
    const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);

    // Delete confirmation dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<MeilensteinItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [formData, setFormData] = useState<MeilensteinForm>({
        itemId: undefined,
        projektId: project?.projektID || 0,
        bezeichnung: '',
        beschreibung: '',
        datum: null,
        insertAfterId: 0,
    });

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch milestones
    const fetchMeilensteine = useCallback(async () => {
        if (!project?.projektID || !dataService) return;

        try {
            setIsLoading(true);
            setError(null);
            const data = await dataService.getMeilensteinListe(project.projektID);
            console.log('Fetched milestones:', data);
            // Sort by OrderBy to ensure correct display order
            const sorted = [...data].sort((a, b) => a.orderBy - b.orderBy);
            setMeilensteine(sorted);
        } catch (err) {
            console.error('Error loading milestones:', err);
            setError('Error loading milestones');
            setMeilensteine([]);
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchMeilensteine();
    }, [fetchMeilensteine]);

    // Get insert after options
    const insertAfterOptions = useMemo(() => {
        if (editingId) {
            // When editing: show other milestones
            return meilensteine
                .filter(m => m.itemId !== editingId)
                .map(m => ({
                    label: `${m.prefixTitle}: ${m.bezeichnung}`,
                    value: m.itemId.toString(),
                }));
        } else {
            // When creating: show all milestones
            return meilensteine.map(m => ({
                label: `${m.prefixTitle}: ${m.bezeichnung}`,
                value: m.itemId.toString(),
            }));
        }
    }, [editingId, meilensteine]);

    // Check if form is valid
    const isFormValid = useMemo(() => {
        if (!formData.bezeichnung || formData.bezeichnung.trim() === '') {
            return false; // Designation is required
        }
        if (!formData.datum) {
            return false; // Date is required
        }
        if (!editingId && (!formData.insertAfterId || formData.insertAfterId <= 0)) {
            return false; // Insert after must be selected in add mode
        }
        return true;
    }, [formData, editingId]);

    const handleAddNew = () => {
        setEditingId(null);
        setIsViewOnly(false);
        setFormData({
            itemId: undefined,
            projektId: project?.projektID || 0,
            bezeichnung: '',
            beschreibung: '',
            datum: null,
            insertAfterId: 0,
        });
        setIsDrawerOpen(true);
    };

    const handleView = (item: any) => {
        setEditingId(item.itemId);
        setIsViewOnly(true);
        setFormData({
            itemId: item.itemId,
            projektId: item.projektId,
            bezeichnung: item.bezeichnung,
            beschreibung: item.beschreibung,
            datum: item.datum ? new Date(item.datum) : null,
            insertAfterId: item.itemId,
        });
        setIsDrawerOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingId(item.itemId);
        setIsViewOnly(false);
        setFormData({
            itemId: item.itemId,
            projektId: item.projektId,
            bezeichnung: item.bezeichnung,
            beschreibung: item.beschreibung,
            datum: item.datum ? new Date(item.datum) : null,
            insertAfterId: item.itemId,
        });
        setIsDrawerOpen(true);
    };

    const handleDelete = async (meilensteinId: number) => {
        const item = meilensteine.find(m => m.itemId === meilensteinId);
        if (item) {
            setItemToDelete(item);
            setDeleteDialogOpen(true);
        }
    };

    const confirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsDeleting(true);
            await dataService.deleteMeilenstein(itemToDelete.itemId);
            await fetchMeilensteine();
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting milestone:', err);
            setError('Error deleting milestone');
        } finally {
            setIsDeleting(false);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, item: any) => {
        setDraggedItem(item);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
        }
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverItem(null);
        setDropPosition(null);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, item: any) => {
        e.preventDefault();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }

        if (!draggedItem || draggedItem.itemId === item.itemId) {
            return;
        }

        setDragOverItem(item);

        // Calculate position (before/after) based on Y position in row
        const rect = e.currentTarget.getBoundingClientRect();
        const midpoint = rect.height / 2;
        const offsetY = e.clientY - rect.top;

        setDropPosition(offsetY < midpoint ? 'before' : 'after');
    };

    const handleDragLeave = () => {
        setDragOverItem(null);
        setDropPosition(null);
    };

    const handleDrop = async (e: React.DragEvent<HTMLTableRowElement>, targetItem: any) => {
        e.preventDefault();

        if (!draggedItem || draggedItem.itemId === targetItem.itemId || !dataService) {
            handleDragEnd();
            return;
        }

        try {
            // Helper function to convert Date to ISO date string
            const dateToISOString = (date: string): string => {
                if (!date) {
                    return '0001-01-01';
                }
                // If it's already an ISO string, extract just the date part
                if (typeof date === 'string' && date.includes('T')) {
                    return date.split('T')[0];
                }
                return date;
            };

            // Determine insertAfterId based on drop position
            let insertAfterId = targetItem.itemId;

            if (dropPosition === 'before') {
                // Find the item before targetItem
                const targetIndex = meilensteine.findIndex(m => m.itemId === targetItem.itemId);
                if (targetIndex > 0) {
                    insertAfterId = meilensteine[targetIndex - 1].itemId;
                } else {
                    // Inserting at the beginning - use -2 as special value
                    insertAfterId = -2;
                }
            }

            const submitData = {
                ItemId: draggedItem.itemId,
                ProjektId: draggedItem.projektId,
                Bezeichnung: draggedItem.bezeichnung,
                Beschreibung: draggedItem.beschreibung,
                Datum: dateToISOString(draggedItem.datum),
            };

            await dataService.updateMeilenstein(submitData, insertAfterId);
            await fetchMeilensteine();
        } catch (err) {
            console.error('Error reordering milestone:', err);
            setError('Error reordering milestone');
        } finally {
            handleDragEnd();
        }
    };

    const handleSave = async () => {
        if (!dataService || !formData.bezeichnung) {
            setError('Please fill in required fields');
            return;
        }

        try {
            setIsLoading(true);

            // Helper function to convert Date to ISO date string
            const dateToISOString = (date: Date | null): string => {
                if (!date) {
                    return '0001-01-01';
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const submitData = {
                ItemId: formData.itemId,
                ProjektId: formData.projektId || project?.projektID || 0,
                Bezeichnung: formData.bezeichnung,
                Beschreibung: formData.beschreibung,
                Datum: dateToISOString(formData.datum),
            };

            if (editingId) {
                await dataService.updateMeilenstein(submitData);
            } else {
                await dataService.addMeilenstein(submitData, formData.insertAfterId || -1);
            }

            setIsDrawerOpen(false);
            await fetchMeilensteine();
            setError(null);
        } catch (err) {
            console.error('Error saving milestone:', err);
            setError('Error saving milestone');
        } finally {
            setIsLoading(false);
        }
    };

    if (!project) return null;

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <Button
                    icon={<Add24Regular />}
                    onClick={handleAddNew}
                    appearance="primary"
                >
                    Add Milestone
                </Button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading milestones..." />
                </div>
            ) : meilensteine.length === 0 ? (
                <div className={styles.emptyState}>No milestones found</div>
            ) : (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell style={{ width: '40px' }}></TableHeaderCell>
                                <TableHeaderCell className={styles.colActions}>Actions</TableHeaderCell>
                                <TableHeaderCell className={styles.colNumber}>Milestone Number</TableHeaderCell>
                                <TableHeaderCell>Designation</TableHeaderCell>
                                <TableHeaderCell className={styles.colDate}>Date</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {meilensteine.map(item => (
                                <TableRow
                                    key={item.itemId}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, item)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, item)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, item)}
                                    style={{
                                        opacity: draggedItem?.itemId === item.itemId ? 0.5 : 1,
                                        backgroundColor: dragOverItem?.itemId === item.itemId ? '#f0f0f0' : 'transparent',
                                        borderTop: dragOverItem?.itemId === item.itemId && dropPosition === 'before' ? '3px solid #107c10' : 'none',
                                        borderBottom: dragOverItem?.itemId === item.itemId && dropPosition === 'after' ? '3px solid #ff8c00' : 'none',
                                        position: 'relative',
                                    }}
                                >
                                    <TableCell style={{ width: '40px' }}>
                                        <span className={styles.dragHandle} title="Drag to reorder">⋮⋮</span>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            icon={<Eye24Regular />}
                                            size="small"
                                            onClick={() => handleView(item)}
                                            title="View"
                                        />
                                        <Button
                                            icon={<Edit24Regular />}
                                            size="small"
                                            onClick={() => handleEdit(item)}
                                            title="Edit"
                                        />
                                        <Button
                                            icon={<Delete24Regular />}
                                            size="small"
                                            onClick={() => handleDelete(item.itemId)}
                                            title="Delete"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.prefixTitle}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.bezeichnung}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {item.datum ? new Date(item.datum).toLocaleDateString() : '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Drawer
                open={isDrawerOpen}
                onOpenChange={(_, { open }) => setIsDrawerOpen(open)}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>
                        {isViewOnly ? 'View Milestone' : (editingId ? 'Edit Milestone' : 'Add Milestone')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {/* Milestone Properties Section */}
                        <div>
                            <div className={styles.sectionHeader}>Milestone Properties</div>
                            <div className={styles.sectionDescription}>
                                Milestone number (automatically assigned), designation, description and date
                            </div>

                            {/* Milestone Number (Read-only) */}
                            {editingId && (
                                <div className={styles.formGroup}>
                                    <Label>Milestone Number</Label>
                                    <div className={styles.readOnlyField}>
                                        {meilensteine.find(m => m.itemId === editingId)?.prefixTitle || '-'}
                                    </div>
                                </div>
                            )}

                            {/* Designation */}
                            <div className={styles.formGroup}>
                                <Label required>Designation</Label>
                                <Input
                                    value={formData.bezeichnung}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, bezeichnung: data.value });
                                    }}
                                    placeholder="Enter designation"
                                />
                            </div>

                            {/* Description */}
                            <div className={styles.formGroup}>
                                <Label>Description</Label>
                                <Textarea
                                    value={formData.beschreibung}
                                    disabled={isViewOnly}
                                    onChange={(_e, data) => {
                                        setFormData({ ...formData, beschreibung: data.value });
                                    }}
                                    placeholder="Enter description"
                                    rows={4}
                                />
                            </div>

                            {/* Date */}
                            <div className={styles.formGroup}>
                                <Label required>Date</Label>
                                <DatePicker
                                    value={formData.datum}
                                    disabled={isViewOnly}
                                    onSelectDate={(date) => {
                                        setFormData({ ...formData, datum: date || null });
                                    }}
                                    placeholder="Select date"
                                />
                            </div>

                            {/* Insert After - only show when not viewing */}
                            {!isViewOnly && !editingId && (
                                <div className={styles.formGroup}>
                                    <Label required>Insert After</Label>
                                    <Select
                                        value={formData.insertAfterId?.toString() || ''}
                                        onChange={(_e, data) => {
                                            setFormData({
                                                ...formData,
                                                insertAfterId: data.value ? parseInt(data.value, 10) : 0,
                                            });
                                        }}
                                    >
                                        {insertAfterOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    <Button
                        appearance="secondary"
                        onClick={() => setIsDrawerOpen(false)}
                    >
                        {isViewOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!isViewOnly && (
                        <Button
                            appearance="primary"
                            onClick={handleSave}
                            disabled={!isFormValid}
                        >
                            Save
                        </Button>
                    )}
                </DrawerFooter>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen}>
                <DialogSurface style={{ minWidth: '480px', maxWidth: '600px' }}>
                    <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                        <DialogTitle>Delete Milestone</DialogTitle>
                        <DialogBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ width: '100%' }}>
                                <p style={{ color: tokens.colorStatusDangerForeground1, fontWeight: 600, marginBottom: '12px' }}>
                                    ⚠️ This action cannot be undone
                                </p>
                                <p>
                                    Are you sure you want to delete this milestone?
                                </p>
                                {itemToDelete && (
                                    <div style={{
                                        backgroundColor: tokens.colorNeutralBackground2,
                                        padding: '12px',
                                        borderRadius: tokens.borderRadiusMedium,
                                        marginTop: '12px',
                                    }}>
                                        <div><strong>Milestone Number:</strong> {itemToDelete.prefixTitle}</div>
                                        <div><strong>Designation:</strong> {itemToDelete.bezeichnung}</div>
                                        <div><strong>Date:</strong> {itemToDelete.datum ? new Date(itemToDelete.datum).toLocaleDateString() : '-'}</div>
                                    </div>
                                )}
                            </div>
                        </DialogBody>
                        <div style={{
                            display: 'flex',
                            gap: tokens.spacingHorizontalM,
                            justifyContent: 'flex-end',
                            padding: tokens.spacingHorizontalL,
                            borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                        }}>
                            <Button
                                appearance="secondary"
                                onClick={cancelDelete}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    </DialogContent>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default ProjektmanagementMeilensteine;
